import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Upload, Download, Eye, Calendar, FileText, FolderPlus, Folder } from 'lucide-react';
import PublicationsDataTable from './PublicationsDataTable.jsx';

const PublicationsManagement = () => {
  const [activeTab, setActiveTab] = useState('list');
  const [publications, setPublications] = useState([]);
  const [rubriques, setRubriques] = useState([]);
  const [selectedRubrique, setSelectedRubrique] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showRubriqueForm, setShowRubriqueForm] = useState(false);
  const [editingPublication, setEditingPublication] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [downloadingRubrique, setDownloadingRubrique] = useState(null);

  // États des formulaires
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    rubrique_id: '',
    published_date: '',
    is_public: true,
    current_file_path: null,
    current_file_name: null
  });
  const [newFile, setNewFile] = useState(null);
  const [deleteFile, setDeleteFile] = useState(false);

  const [rubriqueFormData, setRubriqueFormData] = useState({
    name: '',
    description: ''
  });

  // Charger les données
  const loadData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Rubriques
      const rubriquesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/publications/rubriques`, { headers });
      if (rubriquesResponse.ok) {
        const rubriquesData = await rubriquesResponse.json();
        setRubriques(rubriquesData.rubriques || []);
      }

      // Publications (utile pour certains compteurs seulement)
      const publicationsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/publications`, { headers });
      if (publicationsResponse.ok) {
        const publicationsData = await publicationsResponse.json();
        setPublications(publicationsData.publications || []);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Télécharger tous les PDFs d'une rubrique
  const handleDownloadRubriquePDFs = async (rubriqueId, rubriqueName) => {
    const pdfCount = publications.filter(p => p.rubrique_id === rubriqueId && p.file_path).length;
    if (pdfCount === 0) {
      alert('Aucun PDF dans cette rubrique');
      return;
    }
    
    setDownloadingRubrique(rubriqueId);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/publications/rubriques/${rubriqueId}/download-pdfs`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const blob = await response.blob();
        const pdfsCount = response.headers.get('X-PDFs-Count') || '?';
        const totalPubs = response.headers.get('X-Total-Publications') || '?';
        
        // Créer un lien de téléchargement
        const safeName = rubriqueName.replace(/[^a-zA-Z0-9àâäéèêëïîôùûüç\s-_]/g, '').replace(/\s+/g, '_');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${safeName}_PDFs.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
        
        alert(`✅ ${pdfsCount}/${totalPubs} PDFs téléchargés avec succès !`);
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.detail || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur téléchargement PDFs:', error);
      alert(`❌ Erreur: ${error.message}`);
    } finally {
      setDownloadingRubrique(null);
    }
  };

  // Créer une nouvelle rubrique
  const handleCreateRubrique = async (e) => {
    e.preventDefault();
    if (!rubriqueFormData.name.trim()) { alert('Le nom de la rubrique est requis'); return; }
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/publications/rubriques`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(rubriqueFormData)
      });
      if (response.ok) {
        await loadData();
        setShowRubriqueForm(false);
        setRubriqueFormData({ name: '', description: '' });
        setTableRefreshKey(k => k + 1);
        alert('Rubrique créée avec succès !');
      } else {
        const errorData = await response.text();
        alert(`Erreur lors de la création: ${errorData}`);
      }
    } catch (error) {
      console.error('Erreur création rubrique:', error);
      alert('Erreur lors de la création de la rubrique');
    }
  };

  // Créer / Mettre à jour une publication
  const handleCreatePublication = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.rubrique_id) { alert('Le titre et la rubrique sont requis'); return; }
    try {
      const token = localStorage.getItem('authToken');
      const method = editingPublication ? 'PUT' : 'POST';
      const url = editingPublication 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/publications/${editingPublication.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/publications`;
      
      // Préparer les données de la publication (sans les infos de fichier)
      const publicationData = {
        title: formData.title,
        description: formData.description,
        rubrique_id: formData.rubrique_id,
        published_date: formData.published_date ? new Date(formData.published_date).toISOString() : new Date().toISOString(),
        is_public: formData.is_public
      };
      
      // 1. Sauvegarder les métadonnées de la publication
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(publicationData)
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        alert(`Erreur lors de la sauvegarde: ${errorData}`);
        return;
      }
      
      const savedPublication = await response.json();
      const publicationId = editingPublication ? editingPublication.id : savedPublication.id;
      
      // 2. Gérer la suppression du fichier si demandé
      if (editingPublication && deleteFile && formData.current_file_path) {
        try {
          const deleteResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/publications/${publicationId}/file`,
            {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          if (!deleteResponse.ok) {
            console.error('Erreur lors de la suppression du fichier');
          }
        } catch (error) {
          console.error('Erreur suppression fichier:', error);
        }
      }
      
      // 3. Uploader un nouveau fichier si sélectionné
      if (newFile) {
        try {
          const fd = new FormData();
          fd.append('file', newFile);
          const uploadResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/publications/${publicationId}/upload-pdf`,
            {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: fd
            }
          );
          if (!uploadResponse.ok) {
            const errorData = await uploadResponse.text();
            alert(`Publication sauvegardée mais erreur lors de l'upload du PDF: ${errorData}`);
          }
        } catch (error) {
          console.error('Erreur upload PDF:', error);
          alert('Publication sauvegardée mais erreur lors de l\'upload du PDF');
        }
      }
      
      // 4. Rafraîchir les données et fermer le formulaire
      await loadData();
      setShowForm(false);
      setEditingPublication(null);
      setFormData({ title: '', description: '', rubrique_id: '', published_date: '', is_public: true, current_file_path: null, current_file_name: null });
      setNewFile(null);
      setDeleteFile(false);
      setTableRefreshKey(k => k + 1);
      
      const successMessage = editingPublication 
        ? (deleteFile || newFile ? 'Publication et fichier mis à jour avec succès !' : 'Publication mise à jour !')
        : (newFile ? 'Publication créée et PDF uploadé avec succès !' : 'Publication créée ! Vous pouvez l\'éditer pour ajouter un PDF.');
      alert(successMessage);
      
    } catch (error) {
      console.error('Erreur sauvegarde publication:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  // Upload de PDF
  const handlePdfUpload = async (publicationId, file) => {
    setUploadingFile(publicationId);
    try {
      const token = localStorage.getItem('authToken');
      const fd = new FormData();
      fd.append('file', file);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/publications/${publicationId}/upload-pdf`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
      });
      if (response.ok) { await loadData(); setTableRefreshKey(k => k + 1); alert('PDF uploadé avec succès !'); }
      else { const errorData = await response.text(); alert(`Erreur lors de l'upload: ${errorData}`); }
    } catch (error) {
      console.error('Erreur upload PDF:', error);
      alert('Erreur lors de l\'upload du PDF');
    } finally { setUploadingFile(null); }
  };

  // Supprimer une publication
  const handleDeletePublication = async (publicationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette publication ?')) return;
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/publications/${publicationId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) { await loadData(); setTableRefreshKey(k => k + 1); alert('Publication supprimée !'); }
      else { alert('Erreur lors de la suppression'); }
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  // Supprimer une rubrique
  const handleDeleteRubrique = async (rubriqueId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette rubrique ?')) return;
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/publications/rubriques/${rubriqueId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) { await loadData(); setTableRefreshKey(k => k + 1); alert('Rubrique supprimée !'); }
      else { const errorData = await response.text(); alert(`Erreur: ${errorData}`); }
    } catch (error) {
      console.error('Erreur suppression rubrique:', error);
      alert('Erreur lors de la suppression de la rubrique');
    }
  };

  // Éditer une publication
  const handleEdit = (publication) => {
    const pub = publications.find(p => p.id === publication.id) || publication;
    setEditingPublication(pub);
    setFormData({
      title: pub.title,
      description: pub.description || '',
      rubrique_id: pub.rubrique_id,
      published_date: pub.published_date ? new Date(pub.published_date).toISOString().slice(0, 16) : '',
      is_public: pub.is_public,
      current_file_path: pub.file_url || null,
      current_file_name: pub.file_name || null
    });
    setNewFile(null);
    setDeleteFile(false);
    setShowForm(true);
  };

  // Annuler l'édition
  const handleCancel = () => {
    setShowForm(false); setShowRubriqueForm(false); setEditingPublication(null);
    setFormData({ title: '', description: '', rubrique_id: '', published_date: '', is_public: true, current_file_path: null, current_file_name: null });
    setRubriqueFormData({ name: '', description: '' });
    setNewFile(null);
    setDeleteFile(false);
  };

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: 'rgb(196, 150, 96)' }}></div></div>;
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Gestion des Publications</h1>
        <div className="flex gap-2">
          {!showForm && !showRubriqueForm && (
            <>
              <button 
                onClick={() => setShowRubriqueForm(true)} 
                className="px-4 py-2 rounded-lg flex items-center gap-2 text-white"
                style={{ 
                  backgroundColor: 'rgb(196, 150, 96)',
                  fontFamily: '"Lora", serif'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgb(176, 130, 76)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgb(196, 150, 96)'}
              >
                <FolderPlus className="h-4 w-4" /> Nouvelle Rubrique
              </button>
              <button 
                onClick={() => setShowForm(true)} 
                className="px-4 py-2 rounded-lg flex items-center gap-2 text-white"
                style={{ 
                  backgroundColor: 'rgb(196, 150, 96)',
                  fontFamily: '"Lora", serif'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgb(176, 130, 76)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgb(196, 150, 96)'}
              >
                <Plus className="h-4 w-4" /> Nouvelle Publication
              </button>
            </>
          )}
        </div>
      </div>

      {/* Onglets */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button 
              onClick={() => setActiveTab('list')} 
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'list' ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              style={activeTab === 'list' ? { 
                borderBottomColor: 'rgb(196, 150, 96)', 
                color: 'rgb(196, 150, 96)',
                fontFamily: '"Lora", serif'
              } : { fontFamily: '"Lora", serif' }}
            >
              Publications ({publications.length})
            </button>
            <button 
              onClick={() => setActiveTab('rubriques')} 
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'rubriques' ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              style={activeTab === 'rubriques' ? { 
                borderBottomColor: 'rgb(196, 150, 96)', 
                color: 'rgb(196, 150, 96)',
                fontFamily: '"Lora", serif'
              } : { fontFamily: '"Lora", serif' }}
            >
              Rubriques ({rubriques.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Formulaire nouvelle rubrique */}
      {showRubriqueForm && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Nouvelle Rubrique</h2>
            <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700">✕</button>
          </div>
          <form onSubmit={handleCreateRubrique} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
              <input type="text" value={rubriqueFormData.name} onChange={(e) => setRubriqueFormData(prev => ({ ...prev, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={rubriqueFormData.description} onChange={(e) => setRubriqueFormData(prev => ({ ...prev, description: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" rows="3" />
            </div>
            <div className="flex gap-4 pt-4">
              <button type="submit" className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700">Créer la Rubrique</button>
              <button type="button" onClick={handleCancel} className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600">Annuler</button>
            </div>
          </form>
        </div>
      )}

      {/* Formulaire nouvelle / édition publication */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">{editingPublication ? 'Modifier la Publication' : 'Nouvelle Publication'}</h2>
            <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700">✕</button>
          </div>
          <form onSubmit={handleCreatePublication} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
              <input type="text" value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rubrique *</label>
              <select value={formData.rubrique_id} onChange={(e) => setFormData(prev => ({ ...prev, rubrique_id: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" required>
                <option value="">Sélectionnez une rubrique</option>
                {rubriques.map((rubrique) => (
                  <option key={rubrique.id} value={rubrique.id}>{rubrique.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" rows="3" />
            </div>
            
            {/* Gestion du fichier */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Fichier PDF</label>
              
              {/* Fichier actuel */}
              {formData.current_file_name && !deleteFile && (
                <div className="mb-3 p-3 bg-gray-50 rounded flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-gray-500" />
                    <span className="text-sm text-gray-700">{formData.current_file_name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDeleteFile(true)}
                    className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                  >
                    <Trash2 className="h-4 w-4" />
                    Supprimer
                  </button>
                </div>
              )}
              
              {/* Message si fichier marqué pour suppression */}
              {deleteFile && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  Le fichier sera supprimé lors de l'enregistrement.
                  <button
                    type="button"
                    onClick={() => setDeleteFile(false)}
                    className="ml-2 underline"
                  >
                    Annuler
                  </button>
                </div>
              )}
              
              {/* Upload nouveau fichier */}
              <div>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setNewFile(e.target.files[0])}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded file:border-0
                    file:text-sm file:font-semibold
                    file:text-white
                    hover:file:opacity-90"
                  style={{ 
                    fontFamily: '"Lora", serif'
                  }}
                />
                {newFile && (
                  <p className="mt-2 text-sm text-green-600">
                    Nouveau fichier: {newFile.name}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {editingPublication ? 'Choisir un fichier pour remplacer l\'actuel' : 'Format PDF uniquement'}
                </p>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1"><Calendar className="inline h-4 w-4 mr-1" /> Date de publication</label>
              <input type="datetime-local" value={formData.published_date} onChange={(e) => setFormData(prev => ({ ...prev, published_date: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="is_public" 
                checked={formData.is_public} 
                onChange={(e) => setFormData(prev => ({ ...prev, is_public: e.target.checked }))} 
                className="h-4 w-4 border-gray-300 rounded"
                style={{ accentColor: 'rgb(196, 150, 96)' }}
              />
              <label htmlFor="is_public" className="ml-2 block text-sm text-gray-900" style={{ fontFamily: '"Lora", serif' }}>
                Publication publique (visible sur le site)
              </label>
            </div>
            <div className="flex gap-4 pt-4">
              <button 
                type="submit" 
                className="text-white px-6 py-2 rounded-lg"
                style={{ 
                  backgroundColor: 'rgb(196, 150, 96)',
                  fontFamily: '"Lora", serif'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgb(176, 130, 76)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgb(196, 150, 96)'}
              >
                {editingPublication ? 'Mettre à jour' : 'Créer la Publication'}
              </button>
              <button type="button" onClick={handleCancel} className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600">Annuler</button>
            </div>
          </form>
        </div>
      )}

      {/* Contenu des onglets */}
      {activeTab === 'list' && !showForm && !showRubriqueForm && (
        <>
          {/* Filtre par rubrique */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Filtrer par rubrique :</label>
            <select value={selectedRubrique} onChange={(e) => setSelectedRubrique(e.target.value)} className="w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500">
              <option value="all">Toutes les rubriques</option>
              {rubriques.map((rubrique) => (
                <option key={rubrique.id} value={rubrique.id}>{rubrique.name}</option>
              ))}
            </select>
          </div>

          {/* DataTable Admin */}
          <PublicationsDataTable
            selectedRubrique={selectedRubrique}
            onEdit={handleEdit}
            onDelete={handleDeletePublication}
            refreshKey={tableRefreshKey}
            pageLengthDefault={100}
          />
        </>
      )}

      {activeTab === 'rubriques' && !showForm && !showRubriqueForm && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200"><h2 className="text-lg font-semibold">Rubriques ({rubriques.length})</h2></div>
          {rubriques.length === 0 ? (
            <div className="text-center py-12 text-gray-500"><Folder className="h-12 w-12 mx-auto mb-4 text-gray-400" /><p>Aucune rubrique trouvée</p></div>
          ) : (
            <div className="divide-y divide-gray-200">
              {rubriques.map((rubrique) => (
                <div key={rubrique.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-medium text-gray-900">{rubrique.name}</h3>
                        {rubrique.is_default && (<span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">Par défaut</span>)}
                      </div>
                      {rubrique.description && (<p className="text-gray-600 text-sm mb-2">{rubrique.description}</p>)}
                      <div className="text-xs text-gray-500">Créée le {new Date(rubrique.created_at).toLocaleDateString('fr-FR')}<span className="ml-4">{publications.filter(p => p.rubrique_id === rubrique.id).length} publication(s)</span></div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {/* Bouton télécharger tous les PDFs */}
                      {publications.filter(p => p.rubrique_id === rubrique.id && p.file_path).length > 0 && (
                        <button 
                          onClick={() => handleDownloadRubriquePDFs(rubrique.id, rubrique.name)}
                          disabled={downloadingRubrique === rubrique.id}
                          className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                          title={`Télécharger tous les PDFs (${publications.filter(p => p.rubrique_id === rubrique.id && p.file_path).length})`}
                        >
                          {downloadingRubrique === rubrique.id ? (
                            <>⏳ Téléchargement...</>
                          ) : (
                            <><Download className="h-4 w-4" /> Télécharger PDFs ({publications.filter(p => p.rubrique_id === rubrique.id && p.file_path).length})</>
                          )}
                        </button>
                      )}
                      {!rubrique.is_default && (
                        <button onClick={() => handleDeleteRubrique(rubrique.id)} className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-800"><Trash2 className="h-4 w-4" />Supprimer</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PublicationsManagement;